import type { BedrockPriceOverride, SaveBedrockPriceOverride } from "../../api/bedrockPrices";
import { parseRate, rateToInput } from "./rateInput";

/** The longest source note the server accepts. */
export const MaxSourceLength = 300;

/** What the dialog is for: a new override (the model may be prefilled from the unpriced list), or an existing one. */
export type OverrideDialogMode = { kind: "create"; modelId: string } | { kind: "edit"; override: BedrockPriceOverride };

/** The request fields a message can be shown under. */
export type FieldName = "modelId" | "inputPerMillion" | "outputPerMillion" | "cacheReadPerMillion" | "cacheWritePerMillion" | "source" | "reviewBy";

export const fieldNames: readonly FieldName[] = ["modelId", "inputPerMillion", "outputPerMillion", "cacheReadPerMillion", "cacheWritePerMillion", "source", "reviewBy"];

/** The reason a field is refused, as a key under `bedrockPrices.dialog.errors`. */
export type FieldErrors = Partial<Record<FieldName, string>>;

export interface Draft {
  modelId: string;
  inputPerMillion: string;
  outputPerMillion: string;
  cacheReadPerMillion: string;
  cacheWritePerMillion: string;
  source: string;
  reviewBy: string;
}

export function toDraft(mode: OverrideDialogMode): Draft {
  if (mode.kind === "create") {
    return { modelId: mode.modelId, inputPerMillion: "", outputPerMillion: "", cacheReadPerMillion: "", cacheWritePerMillion: "", source: "", reviewBy: "" };
  }

  const entry = mode.override;
  const optional = (value: number | null): string => (value === null ? "" : rateToInput(value));
  return {
    modelId: entry.modelId,
    inputPerMillion: rateToInput(entry.inputPerMillion),
    outputPerMillion: rateToInput(entry.outputPerMillion),
    cacheReadPerMillion: optional(entry.cacheReadPerMillion),
    cacheWritePerMillion: optional(entry.cacheWritePerMillion),
    source: entry.source ?? "",
    reviewBy: entry.reviewBy ?? "",
  };
}

/**
 * Checks a draft the way the server will (the server stays the authority) and builds the body to send. The model must be a profile id or
 * ARN with a global or eu route, or a regional model id (no profile prefix; other geographic prefixes are refused), input and output are required, every rate is above zero, at most 1000 and has at most six decimal places,
 * and the note is at most 300 characters. Rates come from number inputs, so they are read and sent as plain numbers. A field the browser
 * could not read as a number (`badInput`) is refused as not a number.
 */
export function validateDraft(draft: Draft, version: number | undefined, badInput: ReadonlySet<FieldName> = new Set()): { body: SaveBedrockPriceOverride } | { errors: FieldErrors } {
  const errors: FieldErrors = {};
  const modelId = draft.modelId.trim();
  if (modelId === "") {
    errors.modelId = "required";
  } else if (/(^|[./])(us|apac|jp|au|ca|us-gov|eu-gov)\./i.test(modelId) || !modelId.includes(".") || modelId.startsWith(".")) {
    errors.modelId = "route";
  }

  const rate = (field: FieldName, required: boolean): number | null => {
    if (badInput.has(field)) {
      errors[field] = "number";
      return null;
    }
    const parsed = parseRate(draft[field]);
    switch (parsed.kind) {
      case "ok":
        return parsed.value;
      case "empty":
        if (required) {
          errors[field] = "required";
        }
        return null;
      case "invalid":
        errors[field] = "number";
        return null;
      case "tooManyPlaces":
        errors[field] = "places";
        return null;
      default:
        errors[field] = "range";
        return null;
    }
  };

  const input = rate("inputPerMillion", true);
  const output = rate("outputPerMillion", true);
  const cacheRead = rate("cacheReadPerMillion", false);
  const cacheWrite = rate("cacheWritePerMillion", false);
  if (draft.source.length > MaxSourceLength) {
    errors.source = "sourceLength";
  }

  if (Object.keys(errors).length > 0 || input === null || output === null) {
    return { errors };
  }

  const source = draft.source.trim();
  return {
    body: {
      modelId,
      inputPerMillion: input,
      outputPerMillion: output,
      cacheReadPerMillion: cacheRead,
      cacheWritePerMillion: cacheWrite,
      source: source === "" ? null : source,
      reviewBy: draft.reviewBy === "" ? null : draft.reviewBy,
      ...(version === undefined ? {} : { version }),
    },
  };
}
